import React from 'react';
import { Sparkles, Cpu } from 'lucide-react';
import SelectionCard from '../SelectionCard';
import { matchSuggestedEquipment } from '../../../utils/equipmentMatcher';
import useFrameConfig from '../../../hooks/useFrameConfig';
import { FRAME_MECHANIC_TYPES } from '../../../config/campaignFrameConfig';

/**
 * EquipmentSection - Select weapons and armor
 */
function EquipmentSection({ character, onUpdate, allData }) {
  const weapons = allData.weapons || {};
  const armor = allData.armor || {};

  // Get available equipment based on campaign frame
  const frameData = allData.campaignFrames?.[character.campaignFrame];

  // Check for equipment override (e.g., Motherboard's Ikonis system)
  const { mechanics } = useFrameConfig(character?.campaignFrame);
  const equipmentOverride = Object.entries(mechanics || {}).find(
    ([_, config]) => config.pattern === FRAME_MECHANIC_TYPES.EQUIPMENT_OVERRIDE
  );

  // If this frame uses equipment override, show explanation instead of weapon selection
  if (equipmentOverride) {
    const overrideConfig = equipmentOverride[1];
    return (
      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-br from-purple-900/30 to-gray-900 rounded-lg border border-purple-600/50">
          <div className="flex items-center gap-3 mb-3">
            <Cpu className="w-6 h-6 text-purple-400" />
            <h4 className="text-lg font-bold text-purple-300">
              {overrideConfig.displayName || 'Custom Equipment System'}
            </h4>
          </div>
          <p className="text-gray-300 text-sm mb-3">
            This campaign frame uses a unique equipment system instead of standard weapons and armor.
            You'll customize your equipment on the character sheet after creation.
          </p>
          <div className="p-3 bg-gray-800/50 rounded border border-gray-700">
            <p className="text-xs text-gray-400">
              <strong className="text-purple-300">Motherboard:</strong> Your Ikonis is a bonded weapon that grows with you.
              You'll name it, install augments, and craft new components using scrap resources.
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-500 italic text-center">
          ✓ This section is complete — no equipment selection needed
        </p>
      </div>
    );
  }

  // Default to core equipment if no campaign frame selected
  const campaignFrame = character.campaignFrame || 'core';

  // Handle different weapon structures (core uses object, Beast Feast uses arrays)
  let availableWeapons;
  if (campaignFrame === 'core') {
    availableWeapons = weapons;
  } else if (frameData?.startingEquipment?.primaryWeapons) {
    // Campaign frame with custom weapons: combine primaryWeapons and secondaryWeapons into an object
    const primary = frameData.startingEquipment.primaryWeapons || [];
    const secondary = frameData.startingEquipment.secondaryWeapons || [];
    const allWeapons = [...primary, ...secondary];

    availableWeapons = allWeapons.reduce((obj, weapon) => {
      obj[weapon.id] = weapon;
      return obj;
    }, {});
  } else {
    availableWeapons = weapons; // Fallback to core
  }

  const availableArmor = campaignFrame === 'core'
    ? armor
    : frameData?.startingEquipment?.armor || armor; // Fallback to core armor

  // Get class-specific equipment guidance
  const classGuidance = character.class && allData?.classGuidance?.[character.class];
  const suggestedEquipment = classGuidance?.suggestedEquipment;

  // Prepare equipment data structures for matcher
  const matcherData = React.useMemo(() => {
    if (campaignFrame === 'core') {
      return {
        primaryWeapons: allData.weapons,
        secondaryWeapons: allData.weapons,
        armor: allData.armor
      };
    } else if (frameData?.startingEquipment?.primaryWeapons) {
      return {
        primaryWeapons: frameData.startingEquipment.primaryWeapons || [],
        secondaryWeapons: frameData.startingEquipment.secondaryWeapons || [],
        armor: frameData.startingEquipment.armor || []
      };
    }
    // Fallback to core
    return {
      primaryWeapons: allData.weapons,
      secondaryWeapons: allData.weapons,
      armor: allData.armor
    };
  }, [campaignFrame, frameData, allData.weapons, allData.armor]);

  // Calculate recommended equipment IDs for visual badges
  const recommendedIds = React.useMemo(() => {
    if (!suggestedEquipment) return {};

    const matched = matchSuggestedEquipment(
      suggestedEquipment,
      matcherData.primaryWeapons,
      matcherData.secondaryWeapons,
      matcherData.armor,
      campaignFrame
    );

    return matched || {};
  }, [suggestedEquipment, matcherData, campaignFrame]);

  function handleWeaponSelect(slot, weaponId) {
    onUpdate({
      equipment: {
        ...character.equipment,
        [slot]: weaponId
      }
    });
  }

  function handleArmorSelect(armorId) {
    onUpdate({
      equipment: {
        ...character.equipment,
        armor: armorId
      }
    });
  }

  // Determine weapon structure based on campaign frame
  const usingFrameWeapons = campaignFrame !== 'core' && frameData?.startingEquipment?.primaryWeapons;

  // Get weapons separated by type
  let twoHandedWeapons, primaryWeapons, secondaryWeapons;

  if (usingFrameWeapons) {
    // Beast Feast: primary and secondary are separate arrays
    const allPrimary = frameData.startingEquipment.primaryWeapons || [];
    const allSecondary = frameData.startingEquipment.secondaryWeapons || [];

    // Filter two-handed from primary weapons
    twoHandedWeapons = allPrimary.filter(w =>
      w.burden === 'Two-Handed' || w.burden === 'two-handed'
    );

    // Filter one-handed from primary weapons for primary slot
    primaryWeapons = allPrimary.filter(w =>
      w.burden === 'One-Handed' || w.burden === 'one-handed'
    );

    // Secondary weapons are their own list (shields, paired weapons, etc.)
    secondaryWeapons = allSecondary;
  } else {
    // Core: convert weapons object to array and filter
    const allWeaponsArray = Object.values(availableWeapons);

    twoHandedWeapons = allWeaponsArray.filter(w =>
      w.burden === 'Two-Handed' || w.burden === 'two-handed'
    );

    // For core, primary and secondary use the same one-handed weapons
    const oneHandedWeapons = allWeaponsArray.filter(w =>
      w.burden === 'One-Handed' || w.burden === 'one-handed'
    );
    primaryWeapons = oneHandedWeapons;
    secondaryWeapons = oneHandedWeapons;
  }

  function handleWeaponChoiceChange(choice) {
    onUpdate({
      equipment: {
        ...character.equipment,
        weaponChoice: choice,
        primaryWeapon: '', // Reset selections when changing choice
        secondaryWeapon: ''
      }
    });
  }

  function applySuggestedEquipment() {
    if (!suggestedEquipment) return;

    // Use equipment matcher to get appropriate IDs for current campaign frame
    const matched = matchSuggestedEquipment(
      suggestedEquipment,
      matcherData.primaryWeapons,
      matcherData.secondaryWeapons,
      matcherData.armor,
      campaignFrame
    );

    if (matched) {
      onUpdate({
        equipment: {
          ...character.equipment,
          ...matched
        }
      });
    }
  }

  return (
    <div className="space-y-6">
      {/* Apply Suggested Equipment Button */}
      {suggestedEquipment && (
        <button
          onClick={applySuggestedEquipment}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white rounded-lg font-medium transition-all shadow-lg"
        >
          <Sparkles className="w-5 h-5" />
          Apply Suggested Equipment for {character.class}
        </button>
      )}

      {/* Weapon Choice */}
      <div>
        <h4 className="text-heading-sm text-red-400 font-serif mb-3">Weapon Loadout</h4>
        <div className="grid gap-3 md:grid-cols-2">
          <SelectionCard
            title="One Two-Handed Weapon"
            description="Use a powerful two-handed weapon"
            isSelected={character.equipment?.weaponChoice === 'two-handed'}
            onClick={() => handleWeaponChoiceChange('two-handed')}
          />
          <SelectionCard
            title="Dual Wield"
            description="Use two one-handed weapons (primary and secondary)"
            isSelected={character.equipment?.weaponChoice === 'dual-wield'}
            onClick={() => handleWeaponChoiceChange('dual-wield')}
          />
        </div>
      </div>

      {/* Show weapons based on choice */}
      {character.equipment?.weaponChoice === 'two-handed' && (
        <div>
          <h4 className="text-heading-sm text-red-400 font-serif mb-3">Choose Your Two-Handed Weapon</h4>
          <div className="grid gap-3 md:grid-cols-2">
            {twoHandedWeapons.map((weapon) => (
              <SelectionCard
                key={weapon.id}
                title={weapon.name}
                subtitle={`${weapon.damage} • ${weapon.trait} • ${weapon.range}`}
                description={weapon.feature}
                isSelected={character.equipment?.primaryWeapon === weapon.id}
                isRecommended={weapon.id === recommendedIds.primaryWeapon}
                onClick={() => handleWeaponSelect('primaryWeapon', weapon.id)}
              />
            ))}
          </div>
        </div>
      )}

      {character.equipment?.weaponChoice === 'dual-wield' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Primary Weapon */}
          <div>
            <h4 className="text-heading-sm text-red-400 font-serif mb-3">Primary Weapon</h4>
            <div className="space-y-3">
              {primaryWeapons.map((weapon) => (
                <SelectionCard
                  key={weapon.id}
                  title={weapon.name}
                  subtitle={`${weapon.damage} • ${weapon.trait} • ${weapon.range}`}
                  description={weapon.feature}
                  isSelected={character.equipment?.primaryWeapon === weapon.id}
                  isRecommended={weapon.id === recommendedIds.primaryWeapon}
                  onClick={() => handleWeaponSelect('primaryWeapon', weapon.id)}
                />
              ))}
            </div>
          </div>

          {/* Secondary Weapon */}
          <div>
            <h4 className="text-heading-sm text-red-400 font-serif mb-3">Secondary Weapon</h4>
            <div className="space-y-3">
              {secondaryWeapons.map((weapon) => (
                <SelectionCard
                  key={weapon.id}
                  title={weapon.name}
                  subtitle={`${weapon.damage} • ${weapon.trait} • ${weapon.range}`}
                  description={weapon.feature}
                  isSelected={character.equipment?.secondaryWeapon === weapon.id}
                  isRecommended={weapon.id === recommendedIds.secondaryWeapon}
                  onClick={() => handleWeaponSelect('secondaryWeapon', weapon.id)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Armor */}
      <div>
        <h4 className="text-heading-sm text-red-400 font-serif mb-3">Armor</h4>
        <div className="grid gap-3 md:grid-cols-2">
          {(Array.isArray(availableArmor)
            ? availableArmor.map(armorData => [armorData.id, armorData])
            : Object.entries(availableArmor)
          ).map(([id, armorData]) => (
            <SelectionCard
              key={id}
              title={armorData.name}
              subtitle={`Base Threshold: ${armorData.baseThresholds?.minor || 'N/A'}`}
              description={armorData.description}
              isSelected={character.equipment?.armor === id}
              isRecommended={id === recommendedIds.armor}
              onClick={() => handleArmorSelect(id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default EquipmentSection;
